import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { PageHeader, Form, Input, Button, Spin, message } from 'antd';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolPermission from '../../tools/ToolPermission';

import SpinFull from '../../components/SpinFull';
import UserResetPasswordModal from './UserResetPasswordModal';

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

export default function UserEditScreen() {
  const history = useHistory();
  const { userId } = useParams();
  const userData = useSelector(state => state.user);
  const userPermissionsData = useSelector(state => state.userPermissions);

  const canEditOtherUsers = ToolPermission.hasPermission(userPermissionsData, 'ui:user', 'put:xu');
  const isEditingSelf = userId === userData.id;

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const userRes = await ToolRequest.request('GET', `/v1/user/${userId}`);
      setUser(userRes);
      setLoaded(true);
    })();
  }, [userId]);

  const onFinish = async (values) => {
    setLoading(true);

    try {
      await ToolRequest.request('PUT', `/v1/user/${userId}`, {
        displayName: values.displayName.trim(),
      });

      message.success('Update User successfully');

      history.push('/user');
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          case 'INVALID_PARAMS':
            return message.error('Invalid parameters. Please check your inputs');
          case 'NOT_FOUND':
            return message.error('User not found');
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  if (!canEditOtherUsers && !isEditingSelf) {
    return <SpinFull />;
  }

  return loaded ? (
    <PageHeader
      title={`Edit User (${user.displayName})`}
      onBack={() => history.push('/user')}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          initialValues={user}
          onFinish={onFinish}
        >
          <Form.Item
            label="Username"
            name="name"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[
              {
                required: true,
                message: 'Please enter the display name',
              },
            ]}
          >
            <Input
              placeholder="Display name"
            />
          </Form.Item>

          <Form.Item
            label="Role"
          >
            <Input disabled value={(user.roles || []).join(', ')} />
          </Form.Item>

          {canEditOtherUsers && !isEditingSelf ? (
            <Form.Item {...formTailLayout}>
              <Button onClick={() => setResetPasswordVisible(true)} block>
                Reset Password
              </Button>
            </Form.Item>
          ) : null}

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>

      <UserResetPasswordModal
        visible={resetPasswordVisible}
        userId={userId}
        onOk={() => setResetPasswordVisible(false)}
        onCancel={() => setResetPasswordVisible(false)}
      />
    </PageHeader>
  ) : <SpinFull />;
}
